import { accountsApi } from "../../../api/api";

/**
 * @typedef {Object} Project
 * @property {string} id
 * @property {string} name
 * @property {string} [description]
 * @property {string} [createdAt]
 */

/**
 * @typedef {Object} PageResponse
 * @property {Project[]} content
 * @property {{ pageNumber: number, pageSize: number }} pageable
 * @property {number} totalElements
 */

/**
 * @typedef {Object} Collaborator
 * @property {string} email
 * @property {string} role
 * @property {string} [userId]
 */

/**
 * @typedef {Object} ApiKey
 * @property {string} id
 * @property {string} key
 * @property {string} [createdAt]
 */

const BASE = "/api/projects";

/** @param {Record<string, any>} [filters] @param {{ page?: number, size?: number }} [pagination] @returns {Promise<PageResponse>} */
export const get = (filters = {}, pagination) =>
  accountsApi
    .get(BASE, { params: { ...filters, ...pagination } })
    .then((res) => res.data);

/** @param {string} id @returns {Promise<Project>} */
export const getById = (id) =>
  accountsApi.get(`${BASE}/${id}`).then((res) => res.data);

/** @param {{ name: string, description?: string }} projectData @returns {Promise<Project>} */
export const create = (projectData) =>
  accountsApi.post(BASE, projectData).then((res) => res.data);

/** @param {string} id @param {{ name: string, description?: string }} projectData @returns {Promise<Project>} */
export const update = (id, projectData) =>
  accountsApi
    .put(`${BASE}/${id}`, {
      name: projectData.name,
      description: projectData.description,
    })
    .then((res) => res.data);

/** @param {string} id @returns {Promise<any>} */
export const remove = (id) =>
  accountsApi.delete(`${BASE}/${id}`).then((res) => res.data);

export const deleteById = remove;

/** @param {string} id @returns {Promise<Collaborator[]>} */
export const getCollaborators = (id) =>
  accountsApi.get(`${BASE}/${id}/collaborators`).then((res) => res.data);

/** @param {string} id @param {{ email: string, role?: string }} data @returns {Promise<Collaborator>} */
export const addCollaborator = (id, data) =>
  accountsApi.post(`${BASE}/${id}/collaborators`, data).then((res) => res.data);

/** @param {string} id @param {string} userId @returns {Promise<any>} */
export const removeCollaborator = (id, userId) =>
  accountsApi
    .delete(`${BASE}/${id}/collaborators/${userId}`)
    .then((res) => res.data);

/** @param {string} id @param {string[]} collaborators @returns {Promise<Collaborator[]>} */
export const updateProjectCollaborators = (id, collaborators) =>
  accountsApi
    .put(`${BASE}/${id}/collaborators`, {
      collaborators: collaborators.map((email) => ({ email, role: 'CONTRIBUTOR' })),
    })
    .then((res) => res.data);

export const syncCollaborators = updateProjectCollaborators;

/** @param {string} id @returns {Promise<ApiKey>} */
export const getApiKey = (id) =>
  accountsApi.get(`${BASE}/${id}/api-key`).then((res) => res.data);

/** @param {string} id @returns {Promise<ApiKey>} */
export const createApiKey = (id) =>
  accountsApi.post(`${BASE}/${id}/api-keys`).then((res) => res.data);

/** @param {string} id @param {string} keyId @returns {Promise<any>} */
export const revokeApiKey = (id, keyId) =>
  accountsApi
    .delete(`${BASE}/${id}/api-keys/${keyId}`)
    .then((res) => res.data);
